/**
 * use-edge-swipe.ts — Vuốt từ MÉP TRÁI màn hình sang phải để mở ngăn kéo (bộ lọc Hộp thư di động, kiểu Lark / Gmail).
 *
 * Nghe cảm ứng ở cấp tài liệu (listener thụ động — không chặn cuộn): chỉ khi ngón tay chạm xuống trong ~24px từ mép trái
 * rồi đi sang phải đủ xa và nằm ngang (logic thuần ở lib/gestures.ts `edgeSwipeState`) mới gọi `onOpen`.
 * Cú vuốt dọc / sang trái bị bỏ ngay → cuộn danh sách, kéo-để-làm-mới, vuốt hàng chạy như cũ (các hook đó cũng bỏ qua chạm xuống ở mép).
 *
 * Giới hạn: iOS đã cài PWA (standalone) tắt hẳn — mép trái là cử chỉ "quay lại" của hệ điều hành. Android dùng điều hướng bằng cử chỉ
 * cũng có thể chiếm mép; khi đó trang nhận touchcancel và không mở — nút lọc luôn còn đó.
 */
import { useEffect, useRef } from 'react'
import { edgeSwipeState, edgeSwipeSupported, inEdgeZone, isIosDevice, type EdgeSwipeEnv } from '@/lib/gestures'

/** Đọc môi trường thiết bị (chỉ chạy ở trình duyệt). */
export function readEdgeSwipeEnv(): EdgeSwipeEnv {
  if (typeof navigator === 'undefined') return { ios: false, standalone: false }
  const ios = isIosDevice(navigator.userAgent, navigator.maxTouchPoints)
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
  return { ios, standalone }
}

export function useEdgeSwipe(onOpen: () => void, enabled: boolean) {
  const openRef = useRef(onOpen)
  openRef.current = onOpen

  useEffect(() => {
    if (!enabled || typeof document === 'undefined' || !edgeSwipeSupported(readEdgeSwipeEnv())) return
    let start: { x: number; y: number; id: number } | null = null

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0]
      // Nhiều ngón (phóng to…) hoặc không bắt đầu ở mép → bỏ.
      start = e.touches.length === 1 && inEdgeZone(t.clientX) ? { x: t.clientX, y: t.clientY, id: t.identifier } : null
    }
    const onMove = (e: TouchEvent) => {
      if (!start) return
      const t = Array.from(e.touches).find((x) => x.identifier === start!.id)
      if (!t) return
      const state = edgeSwipeState(t.clientX - start.x, t.clientY - start.y)
      if (state === 'pending') return
      start = null
      if (state === 'open') openRef.current()
    }
    const reset = () => { start = null }

    document.addEventListener('touchstart', onStart, { passive: true })
    document.addEventListener('touchmove', onMove, { passive: true })
    document.addEventListener('touchend', reset, { passive: true })
    document.addEventListener('touchcancel', reset, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onStart)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', reset)
      document.removeEventListener('touchcancel', reset)
    }
  }, [enabled])
}
